<template>
  <div :class="columnClassName">
    <column-header :activeLayout="activeLayout" :columns="columns" :columnIndex="columnIndex" :components="components" :activeComponent="component" v-on:setActiveComponent="setActiveComponent" v-if="component"></column-header>
    <div class="tabulator-parent group-editor">
      <div class="row">
        <div class="col-md-12">
          <h3 v-if="group.name">{{ group.name }}</h3>
          <h3 v-else>{{ _('New user group') }}</h3>
        </div>
      </div>
      <div v-if="!!parseInt(group.allusers)" class="row">
        <div class="col-md-12">
          <div class="alert alert-info">
            <i class="fa fa-fw fa-users"></i>
            {{ _('This user group automatically includes all users. Its membership cannot be edited.') }}
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col-md-12">
          <div class="form-group">
            <label>{{ _('Name') }}</label>
            <input type="text" class="form-control" v-model="group.name" :disabled="!!parseInt(group.allusers)" :placeholder="_('User group name')" />
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col-md-12">
          <div class="form-group">
            <label>{{ _('Description') }}</label>
            <textarea class="form-control" v-model="group.description" rows="3" :disabled="!!parseInt(group.allusers)" :placeholder="_('Optional description')"></textarea>
          </div>
        </div>
      </div>
      <CollectionMemberList
        v-if="!parseInt(group.allusers)"
        :key="'group-members-' + (group.id || 'new')"
        ref="members"
        :details-trail="componentTrail"
        :group-id="group.id || null"
        :groups="group.id ? [group] : []"
        :context-label="group.name || _('New user group')"
        :pending-invites="pendingInvites"
        :empty-hint="group.id ? '' : _('Save the user group first to add existing users. Invitations are sent when you save.')"
        v-on:queued="queueInvite"
        v-on:remove-pending="removePendingInvite"
      />
    </div>
    <nav class="save-bar">
      <div class="row">
        <div class="col-md-12">
          <div class="pull-right">
            <button class="btn btn-danger" v-if="group.id && !parseInt(group.allusers)" v-on:click="remove" :disabled="saving"><i class="fa fa-fw fa-trash"></i> {{ _('Delete') }}</button>
            <button class="btn btn-default" v-on:click="close"><i class="fa fa-fw fa-close"></i> {{ _('Close') }}</button>
            <button class="btn btn-success" v-on:click="save" :disabled="!isSaveButtonEnabled || !!parseInt(group.allusers)"><i :class="'fa fa-fw fa-' + saveButtonIcon"></i> {{ saveButtonText }}</button>
          </div>
        </div>
      </div>
    </nav>
  </div>
</template>

<script>
import axios from 'axios'
import changewatcher from '~/lib/changewatcher'
import components from '~/lib/components'
import memberInvites from '~/lib/memberInvites'
import CollectionMemberList from '~/components/CollectionMemberList'

export default {
  mixins: [changewatcher, components, memberInvites],
  components: { CollectionMemberList },
  data () {
    return {
      group: {
        name: '',
        description: '',
        allusers: 0
      }
    }
  },
  methods: {
    getInviteGroupId () {
      return this.group.id || null
    },
    getInviteMembers () {
      return []
    },
    getInviteContextLabel () {
      return this.group.name || this._('New user group')
    },
    resetGroup () {
      this.group = {
        name: '',
        description: '',
        allusers: 0
      }
      this.pendingInvites = []
      this.storeState()
    },
    async loadGroup (id) {
      if (!id) {
        return
      }
      this.loading = true
      try {
        const response = await axios.get(this.baseDomain + this.shopId + '/usergroups/' + id + '.json?apikey=' + this.apiKey)
        this.group = response.data
      } catch (e) {
        this.showError(e)
      }
      this.storeState()
      this.loading = false
    },
    getSaveData () {
      if (!this.group) return {}
      return {
        id: this.group.id || null,
        name: this.group.name || '',
        description: this.group.description || ''
      }
    },
    async save () {
      if (!this.group.name) {
        return this.showError(this._('Please enter a name for the user group'))
      }
      this.saving = true
      try {
        const data = this.getSaveData()
        let response
        if (data.id) {
          response = await axios.put(
            this.baseDomain + this.shopId + '/usergroups/' + data.id + '.json?apikey=' + this.apiKey,
            data
          )
        } else {
          delete data.id
          response = await axios.post(
            this.baseDomain + this.shopId + '/usergroups.json?apikey=' + this.apiKey,
            data
          )
        }
        const groupId = response.data?.id ?? response.data
        if (groupId) {
          if (this.component && this.component.initData) {
            this.component.initData.groupId = groupId
          }
          await this.flushPendingInvites(groupId)
          await this.loadGroup(groupId)
        }
        this.saved = true
        this.storeState()
        this.showNotification(this._('Changes saved successfully!'))
        this.$emit('save', this.group)
        setTimeout(() => { this.saved = false }, 1000)
      } catch (e) {
        this.showError(e)
      }
      this.saving = false
    },
    async remove () {
      if (!confirm(this._('Delete this user group?'))) return
      this.saving = true
      try {
        await axios.delete(this.baseDomain + this.shopId + '/usergroups/' + this.group.id + '.json?apikey=' + this.apiKey)
        this.$emit('remove', this.group)
        this.close()
      } catch (e) {
        this.showError(e)
      }
      this.saving = false
    }
  },
  created () {
    if (this.component && this.component.initData && !this.loading) {
      const groupId = this.component.initData.groupId
      if (groupId) {
        this.loadGroup(groupId)
      } else {
        this.storeState()
      }
    }
  },
  mounted () {
    if (!this.origState && this.component && this.component.initData && !this.component.initData.groupId && !this.loading) {
      this.storeState()
    }
  },
  async updated () {
    if (this.component && this.component.initData && !this.loading) {
      const initId = parseInt(this.component.initData.groupId, 10) || null
      const currentId = this.group && this.group.id ? parseInt(this.group.id, 10) : null
      if (initId === currentId) {
        return
      }
      if (initId) {
        try {
          await this.loadGroup(initId)
        } catch (e) {}
      } else {
        this.resetGroup()
      }
    }
  }
}
</script>

<style scoped>
.group-editor {
  flex: 1 1 auto;
  min-height: 0;
}
</style>
